import { ParsedInitData, WebAppUser } from '@/types'
import type { PopupParams, WebApp as TgWebApp, WebAppInitData } from 'telegram-web-app'

type WebAppLike = TgWebApp & {
    // Backward-compat aliases used in our wrapper
    hapticFeedback?: {
        selectionChanged(): void
        notificationOccurred(type: 'error' | 'success' | 'warning'): void
        impactOccurred(type: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void
    }
    cloudStorage?: {
        setItem(
            key: string,
            value: string,
            cb: (error: string | null, result: boolean) => void
        ): void
        getItem(key: string, cb: (error: string | null, result: string | null) => void): void
        removeItem(key: string, cb: (error: string | null, result: boolean) => void): void
    }
}

export class TelegramWebApp {
    private static instance: TelegramWebApp
    private webApp: WebAppLike | undefined

    private constructor() {
        this.webApp = window.Telegram?.WebApp as unknown as WebAppLike | undefined
    }

    public static getInstance(): TelegramWebApp {
        if (!TelegramWebApp.instance) {
            TelegramWebApp.instance = new TelegramWebApp()
        }
        return TelegramWebApp.instance
    }

    public init(): void {
        if (!this.webApp) return
        this.webApp.ready()
        this.webApp.expand()
    }

    public getInitData(): string {
        return this.webApp?.initData || ''
    }

    public getInitDataUnsafe(): ParsedInitData {
        const data = (this.webApp?.initDataUnsafe || {}) as WebAppInitData
        return {
            user: data.user ? JSON.stringify(data.user) : '',
            auth_date: data.auth_date || 0,
            hash: data.hash || '',
            chat_instance: data.chat_instance,
            chat_type: data.chat_type,
            chat: data.chat ? JSON.stringify(data.chat) : undefined,
            start_param: data.start_param,
            can_send_after: data.can_send_after,
        }
    }

    public getUser(): WebAppUser | null {
        return this.webApp?.initDataUnsafe?.user || null
    }

    public getThemeParams() {
        return this.webApp?.themeParams || {}
    }

    public getColorScheme(): 'light' | 'dark' {
        return this.webApp?.colorScheme || 'light'
    }

    public isExpanded(): boolean {
        return this.webApp?.isExpanded || false
    }

    public getViewportHeight(): number {
        return this.webApp?.viewportHeight || 0
    }

    public getViewportStableHeight(): number {
        return this.webApp?.viewportStableHeight || 0
    }

    public close(): void {
        this.webApp?.close()
    }

    public showAlert(message: string): Promise<void> {
        return new Promise((resolve) => {
            if (this.webApp?.showAlert) {
                this.webApp.showAlert(message, resolve)
            } else {
                alert(message)
                resolve()
            }
        })
    }

    public showConfirm(message: string): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.webApp?.showConfirm) {
                this.webApp.showConfirm(message, resolve)
            } else {
                resolve(confirm(message))
            }
        })
    }

    public showPopup(params: PopupParams): Promise<string | null> {
        return new Promise((resolve) => {
            if (this.webApp?.showPopup) {
                this.webApp.showPopup(params, (buttonId: string) => {
                    resolve(buttonId || null)
                })
            } else {
                resolve(confirm(params.message) ? 'ok' : null)
            }
        })
    }

    public openLink(url: string, options?: { try_instant_view?: boolean }): void {
        if (this.webApp?.openLink) {
            this.webApp.openLink(url, options)
        } else {
            window.open(url, '_blank')
        }
    }

    public openTelegramLink(url: string): void {
        if (this.webApp?.openTelegramLink) {
            this.webApp.openTelegramLink(url)
        } else {
            window.open(url, '_blank')
        }
    }

    public hapticFeedback(
        type:
            | 'light'
            | 'medium'
            | 'heavy'
            | 'rigid'
            | 'soft'
            | 'error'
            | 'success'
            | 'warning'
            | 'selection'
    ): void {
        if (!this.webApp?.hapticFeedback) return

        if (type === 'selection') {
            this.webApp.hapticFeedback.selectionChanged()
        } else if (['error', 'success', 'warning'].includes(type)) {
            this.webApp.hapticFeedback.notificationOccurred(type as 'error' | 'success' | 'warning')
        } else {
            this.webApp.hapticFeedback.impactOccurred(
                type as 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
            )
        }
    }

    public setMainButton(params: {
        text: string
        color?: string
        textColor?: string
        isActive?: boolean
        isVisible?: boolean
    }): void {
        if (!this.webApp?.MainButton) return

        this.webApp.MainButton.setText(params.text)
        if (params.color) this.webApp.MainButton.color = params.color
        if (params.textColor) this.webApp.MainButton.textColor = params.textColor
        if (params.isActive !== undefined) {
            if (params.isActive) {
                this.webApp.MainButton.enable()
            } else {
                this.webApp.MainButton.disable()
            }
        }
        if (params.isVisible !== undefined) {
            if (params.isVisible) {
                this.webApp.MainButton.show()
            } else {
                this.webApp.MainButton.hide()
            }
        }
    }

    public onMainButtonClick(callback: () => void): void {
        this.webApp?.MainButton?.onClick(callback)
    }

    public offMainButtonClick(callback: () => void): void {
        this.webApp?.MainButton?.offClick(callback)
    }

    public setBackButton(isVisible: boolean): void {
        if (!this.webApp?.BackButton) return

        if (isVisible) {
            this.webApp.BackButton.show()
        } else {
            this.webApp.BackButton.hide()
        }
    }

    public onBackButtonClick(callback: () => void): void {
        this.webApp?.BackButton?.onClick(callback)
    }

    public offBackButtonClick(callback: () => void): void {
        this.webApp?.BackButton?.offClick(callback)
    }

    public showScanQrPopup(params: { text?: string }): Promise<string | null> {
        return new Promise((resolve) => {
            if (this.webApp?.showScanQrPopup) {
                this.webApp.showScanQrPopup(params, (text: string) => {
                    resolve(text || null)
                })
            } else {
                resolve(null)
            }
        })
    }

    public closeScanQrPopup(): void {
        if (this.webApp?.closeScanQrPopup) {
            this.webApp?.closeScanQrPopup()
        }
    }

    public readTextFromClipboard(): Promise<string | null> {
        return new Promise((resolve) => {
            if (this.webApp?.readTextFromClipboard) {
                this.webApp.readTextFromClipboard((text: string | null) => {
                    resolve(text || null)
                })
            } else {
                resolve(null)
            }
        })
    }

    public requestWriteAccess(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.webApp?.requestWriteAccess) {
                this.webApp.requestWriteAccess((granted: boolean) => {
                    resolve(granted)
                })
            } else {
                resolve(false)
            }
        })
    }

    public requestContact(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.webApp?.requestContact) {
                this.webApp.requestContact((granted: boolean) => {
                    resolve(granted)
                })
            } else {
                resolve(false)
            }
        })
    }

    public setCloudStorageItem(key: string, value: string): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.webApp?.cloudStorage?.setItem) {
                this.webApp.cloudStorage.setItem(
                    key,
                    value,
                    (error: string | null, result: boolean) => {
                        resolve(!error && result)
                    }
                )
            } else {
                resolve(false)
            }
        })
    }

    public getCloudStorageItem(key: string): Promise<string | null> {
        return new Promise((resolve) => {
            if (this.webApp?.cloudStorage?.getItem) {
                this.webApp.cloudStorage.getItem(
                    key,
                    (error: string | null, result: string | null) => {
                        resolve(error ? null : result)
                    }
                )
            } else {
                resolve(null)
            }
        })
    }

    public removeCloudStorageItem(key: string): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.webApp?.cloudStorage?.removeItem) {
                this.webApp.cloudStorage.removeItem(
                    key,
                    (error: string | null, result: boolean) => {
                        resolve(!error && result)
                    }
                )
            } else {
                resolve(false)
            }
        })
    }
}

export const telegramWebApp = TelegramWebApp.getInstance()
